import * as React from "react";
import styles from "./CookieConsent.module.css";
import {
  type CookiePreferences,
  defaultPreferences,
  useCookieConsent,
} from "~/contexts/cookieConsentContext";

export const CookieConsent: React.FC = () => {
  const {
    consentState,
    preferences,
    acceptAll,
    declineAll,
    savePreferences,
    _showBanner,
  } = useCookieConsent();

  const [showPreferences, setShowPreferences] = React.useState(false);
  const [localPreferences, setLocalPreferences] =
    React.useState<CookiePreferences>(defaultPreferences);

  // Sync local preferences with context preferences
  React.useEffect(() => {
    setLocalPreferences(preferences);
  }, [preferences]);

  // Use internal _showBanner state if available, otherwise fall back to checking consentState
  const isVisible = _showBanner ?? consentState === null;

  const handlePreferenceChange = (key: keyof CookiePreferences) => {
    if (key === "necessary") return; // Can't disable necessary cookies
    setLocalPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSavePreferences = () => {
    savePreferences(localPreferences);
    setShowPreferences(false);
  };

  if (!isVisible) return null;

  return (
    <div className={styles.banner} role="dialog" aria-label="Cookie consent">
      <div className={styles.container}>
        <div className={styles.content}>
          <h2 className={styles.title}>We value your privacy</h2>
          <p className={styles.description}>
            We use cookies to enhance your browsing experience, serve
            personalized content, and analyze our traffic. By clicking
            &quot;Accept All&quot;, you consent to our use of cookies. Read our{" "}
            <a href="/legal/cookie-policy" className={styles.link}>
              Cookie Policy
            </a>{" "}
            for more information.
          </p>

          {showPreferences && (
            <div className={styles.preferencesPanel}>
              <div className={styles.preferencesGrid}>
                <div className={styles.preferenceItem}>
                  <input
                    type="checkbox"
                    id="necessary"
                    checked={localPreferences.necessary}
                    disabled
                    className={styles.checkbox}
                  />
                  <label htmlFor="necessary" className={styles.preferenceLabel}>
                    <span className={styles.preferenceName}>
                      Necessary{" "}
                      <span className={styles.requiredBadge}>Required</span>
                    </span>
                    <span className={styles.preferenceDescription}>
                      Essential for the website to function properly
                    </span>
                  </label>
                </div>

                <div className={styles.preferenceItem}>
                  <input
                    type="checkbox"
                    id="analytics"
                    checked={localPreferences.analytics}
                    onChange={() => handlePreferenceChange("analytics")}
                    className={styles.checkbox}
                  />
                  <label htmlFor="analytics" className={styles.preferenceLabel}>
                    <span className={styles.preferenceName}>
                      Performance Cookies
                    </span>
                    <span className={styles.preferenceDescription}>
                      Help us understand how visitors interact with our site
                    </span>
                  </label>
                </div>

                <div className={styles.preferenceItem}>
                  <input
                    type="checkbox"
                    id="marketing"
                    checked={localPreferences.marketing}
                    onChange={() => handlePreferenceChange("marketing")}
                    className={styles.checkbox}
                  />
                  <label htmlFor="marketing" className={styles.preferenceLabel}>
                    <span className={styles.preferenceName}>
                      Targeting Cookies
                    </span>
                    <span className={styles.preferenceDescription}>
                      Used for personalization
                    </span>
                  </label>
                </div>

                <div className={styles.preferenceItem}>
                  <input
                    type="checkbox"
                    id="functional"
                    checked={localPreferences.functional}
                    onChange={() => handlePreferenceChange("functional")}
                    className={styles.checkbox}
                  />
                  <label
                    htmlFor="functional"
                    className={styles.preferenceLabel}
                  >
                    <span className={styles.preferenceName}>Functional</span>
                    <span className={styles.preferenceDescription}>
                      Enable enhanced functionality like live chat
                    </span>
                  </label>
                </div>
              </div>

              <button
                onClick={handleSavePreferences}
                className={styles.savePreferences}
              >
                Save Preferences
              </button>
            </div>
          )}
        </div>

        <div className={styles.actions}>
          {!showPreferences && (
            <button
              onClick={() => setShowPreferences(true)}
              className={styles.preferencesButton}
            >
              Customize
            </button>
          )}
          <button onClick={declineAll} className={styles.declineButton}>
            Decline
          </button>
          <button onClick={acceptAll} className={styles.acceptButton}>
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
